// components/LeftPanel.js
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { formatDate, formatDay } from "utils/helpers";
import FileView from "components/fileView";
import { useTranslation } from "react-i18next";
import { LoadingIcon } from "utils/constant";
import ShowUI, { CanShow, LEGISLATIVE_DRAFTING_UI_RULES } from "utils/authRules";
import { updateLegislativeDrafting } from "services/legislativeService";
import { useToast } from "contexts/ToastContext";
import { SECTOR_DIRECTIVES_REQUEST_STATUS } from "utils/constantValues";
import HeadRemark from "./headRemark";
import DirectorRemark from "./directorRemark";
import CoordinatorRemark from "./coordinatorRemark";
import ProsecutorRemark from "./prosecutorRemark";
import ActionAvatars from "pages/conductLegalResearch/fragments/actionAvatars";
import PublicFeedback from "./publicFeedback";
import UploadAmendedFile from "./uploadAmendedFile";
import DynamicFormInput from "components/form/form";
import { useAuth } from "contexts/AuthContext";
import { StatusChip } from "components/StatusChip";
import RespondBackForm from "pages/conductLegalResearch/fragments/respondBackform";
import LegislativeRespondBackForm from "./LegislativeResponback";
import { updateLegalAdvisory } from "services/legalAdvisoryService";
import { updateRegisterDirective } from "services/registerDirectivesService";
import TeamView from "components/team_view";
import DocumentViewerModal from "components/DocumentViewer";
import ConfirmationModal1 from "components/ConfirmationModal1";
import UploadDraftLaw from "./uploadDraftLaw";
import BroadcastCard from "pages/conductLegalResearch/fragments/broadcastCard";
import { t } from "i18next";

function LegalAdvisoryDetail({ request }: { request: any }) {
    const navigate = useNavigate();
    const [loading, setLoading] = useState(false);
    const [publicFeedback, setPublicFeedback] = useState(false);
    const [uploadAmend, setUploadAmend] = useState(false);
    const [auditDraft, setAuditDraft] = useState(false);
    const [showDraftHistory, setShowDraftHistory] = useState(false);
    const [edit, setEdit] = useState(false);
    const [formState, setFormState] = useState({ ...request });
    const contextData = useAuth();
    const [viewTeam, setViewTeam] = useState(false);
    const [editSummary, setEditSummary] = useState(false);
    const [showPreviousFiles, setShowPreviousFiles] = useState(false);
    const { showToast } = useToast();

    const [isRespondBackFormOpen, setIsRespondBackFormOpen] = useState(false); // State to control modal visibility
    
    const [isModalOpen, setIsModalOpen] = useState(false);
    
    const [confirmAction, setConfirmAction] = useState<() => void>(() => {});
    
    const handleFileChange = (
        file: any,
        fieldName: keyof Pick<
            typeof formState,
            "audit_summary_file"
        >
    ) => {
        setFormState((prev: any) => ({
            ...prev,
            [fieldName]: file,
        }));
    };

    const handleChange = (
        e: React.ChangeEvent<
            HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement
        >
    ) => {
        const { name, value } = e.target;
        setFormState((prev: any) => ({
            ...prev,
            [name]: value,
        }));
    };

    const handleRespondBackClick = () => {
        setIsRespondBackFormOpen(!isRespondBackFormOpen);
    }

    const handleAdditionalInfoApprove = async () => {
        setLoading(true);
        const data = new FormData();

        data.append("forward_status", SECTOR_DIRECTIVES_REQUEST_STATUS.forwarded_to_registrar.value);

        try {
            const response = await updateRegisterDirective(contextData.token || contextData?.user?.access_token || "", formState.id, data);

            setFormState((prev: any) => ({ ...prev, ...response }));
            showToast("Legal research proposal Approved!", "success");
            // Manually clear file inputs
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast("Failed to update legal research proposal Approval.", "error");
        } finally {
            setLoading(false);
        }
    }

    const handleReject = () => {
    }

    const handleForward = async (froward_status: string) => {
        const data = new FormData();
        if (formState.status === "Under review" && froward_status === SECTOR_DIRECTIVES_REQUEST_STATUS.forwarded_to_head.value) {
            showToast(t("CannotForwardBeforeEditing"), "error");
            return;
        }
        if (formState.status === "Back to Head" && froward_status === SECTOR_DIRECTIVES_REQUEST_STATUS.forwarded_to_director.value) {
            showToast(t("CannotForwardBeforeEditing"), "error");
            return;
        }

        if (formState.status === "Back to Director" && froward_status === SECTOR_DIRECTIVES_REQUEST_STATUS.forwarded_to_coordinator.value) {
            showToast(t("CannotForwardBeforeEditing"), "error");
            return;
        }

        data.append(
            "forward_status",
            froward_status
        );

        try {
            const response = await updateRegisterDirective(contextData.token || contextData?.user?.access_token || "", formState.id, data);

            setFormState((prev: any) => ({ ...prev, ...response }));
            showToast(t("LegislativeDraftForwardedSuccess"), "success");
            // Manually clear file inputs
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToUpdateLegislativeDraftRequest"), "error");
        } finally {
            setLoading(false);
        }
    };

    const handleForwardToStakeholder = async () => {
        const data = new FormData();
        data.append("status", SECTOR_DIRECTIVES_REQUEST_STATUS.additional_info_requested.value);
        data.append("forward_status", SECTOR_DIRECTIVES_REQUEST_STATUS.assigned_to_prosecutor.value);

        try {
            const response = await updateRegisterDirective(contextData.token || contextData?.user?.access_token || "", formState.id, data);
            setFormState((prev: any) => ({ ...prev, ...response }));
            showToast(t("LegislativeDraftForwardedSuccess"), "success");
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToUpdateLegislativeDraftRequest"), "error");
        } finally {
            setLoading(false);
        }
    };

    const handleAudit = async () => {
        const data = new FormData();
        data.append("audit_summary", formState.audit_summary);
        data.append("audit_summary_file", formState.audit_summary_file);
        data.append("status", SECTOR_DIRECTIVES_REQUEST_STATUS.audit_submitted.value);

        try {
            const response = await updateRegisterDirective(contextData.token || contextData?.user?.access_token || "", formState.id, data);

            setFormState((prev: any) => ({ ...prev, ...response }));
            showToast("Legislative Draft Forwarded successfully!", "success");
            // Manually clear file inputs
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast("Failed to update Legislative Draft request.", "error");
        } finally {
            setLoading(false);
            setAuditDraft(false);
        }
    };

    const handleBroadcast = async (isPublished: boolean) => {
        const data = new FormData();
        data.append("is_published", isPublished.toString());

        try {
            const response = await updateRegisterDirective(contextData.token || contextData?.user?.access_token || "", formState.id, data);

            setFormState((prev: any) => ({ ...prev, is_published: isPublished }));
            showToast(isPublished ? "Directive broadcasted to public website!" : "Directive unpublished!", "success");
        } catch (error) {
            console.error("Error updating broadcast status:", error);
            showToast("Failed to update broadcast status.", "error");
        } finally {
            setLoading(false);
        }
    };

    const handleDraftApprove = async () => {
        const data = new FormData();
        data.append("status", SECTOR_DIRECTIVES_REQUEST_STATUS.draft_approved.value);
        data.append("forward_status", SECTOR_DIRECTIVES_REQUEST_STATUS.forwarded_to_registrar.value);

        try {
            const response = await updateRegisterDirective(contextData.token || contextData?.user?.access_token || "", formState.id, data);

            setFormState((prev: any) => ({ ...prev, ...response }));
            showToast(t("LegislativeDraftForwardedSuccess"), "success");
            // Manually clear file inputs
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToUpdateLegislativeDraftRequest"), "error");
        } finally {
            setLoading(false);
        }
    };

    const handleFinalApprove = async () => {
        const data = new FormData();
        data.append("status", SECTOR_DIRECTIVES_REQUEST_STATUS.final_approval.value);

        try {
            const response = await updateRegisterDirective(contextData.token || contextData?.user?.access_token || "", formState.id, data);

            setFormState((prev: any) => ({ ...prev, ...response }));
            showToast(t("LegislativeDraftForwardedSuccess"), "success");
            // Manually clear file inputs
        } catch (error) {
            console.error("Error submitting request:", error);
            showToast(t("FailedToUpdateLegislativeDraftRequest"), "error");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { console.log("formState: ", formState) }, [formState]);

    return (
        <div className="flex-1 bg-gray-100 mx-4 rounded-lg overflow-y-scroll scrollbar-hide">
            <div className="flex flex-col max-w-4xl bg-white rounded-lg shadow-md p-6">
                {/* Header */}
                <div className="flex items-center justify-between pb-4 mb-3">
                    <div className="flex items-center space-x-2">
                        <button
                            onClick={() => navigate("/registering-directives")}
                            className="text-gray-600 hover:text-gray-800"
                        >
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                className="h-6 w-6"
                                fill="none"
                                viewBox="0 0 24 24"
                                stroke="currentColor"
                            >
                                <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M15 19l-7-7 7-7"
                                />
                            </svg>
                        </button>
                    </div>
                    <h1 className="text-2xl font-semibold text-gray-800 ml-8">
                        {t("RegisterDirective")}
                    </h1>
                    <div className="ml-auto">
                        <StatusChip status={formState.status} />
                        <StatusChip status={formState.forward_status} />
                    </div>
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.registrar_forward_to_coordinator.value}
                        currentStatus={formState.status}
                        forwardStatus={formState.forward_status}
                    >
                        <div className="ml-auto flex gap-4">
                            <button onClick={() => { setConfirmAction(() => () => handleForward(SECTOR_DIRECTIVES_REQUEST_STATUS.forwarded_to_coordinator.value)); setIsModalOpen(true); }} type="button" className="bg-[#0A1D39] text-white rounded-lg px-4">{t("Forward")}</button>
                        </div>
                    </ShowUI>
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.stakeholder_forward_to_head.value}
                        currentStatus={formState.status}
                        forwardStatus={formState.forward_status}
                    >
                        <div className="flex ml-auto gap-4">
                            <div onClick={() => navigate("/registering-directives/request", { state: { request: formState } })} className="px-2 hover:bg-gray-100 rounded-lg cursor-pointer">
                                <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M3.66406 21V16.75L16.8641 3.575C17.0641 3.39167 17.2851 3.25 17.5271 3.15C17.7691 3.05 18.0231 3 18.2891 3C18.5551 3 18.8134 3.05 19.0641 3.15C19.3147 3.25 19.5314 3.4 19.7141 3.6L21.0891 5C21.2891 5.18333 21.4351 5.4 21.5271 5.65C21.6191 5.9 21.6647 6.15 21.6641 6.4C21.6641 6.66667 21.6184 6.921 21.5271 7.163C21.4357 7.405 21.2897 7.62567 21.0891 7.825L7.91406 21H3.66406ZM18.2641 7.8L19.6641 6.4L18.2641 5L16.8641 6.4L18.2641 7.8Z" fill="black" />
                                </svg>
                            </div>
                            <div className="flex gap-4">
                                <button onClick={() => { setConfirmAction(() => () => handleForward(SECTOR_DIRECTIVES_REQUEST_STATUS.forwarded_to_head.value)); setIsModalOpen(true); }} type="button" className="bg-[#0A1D39] text-white rounded-lg px-4">{t("Forward")}</button>
                            </div>
                        </div>
                    </ShowUI>
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.head_forward_to_director.value}
                        currentStatus={formState.status}
                        forwardStatus={formState.forward_status}
                    >
                        <div className="flex ml-auto gap-4">
                            <ShowUI
                                ui_key={LEGISLATIVE_DRAFTING_UI_RULES.edit_head_forward_to_director.value}
                                currentStatus={formState.status}
                                forwardStatus={formState.forward_status}
                            >
                                <div onClick={() => setEdit(!edit)} className="px-2 hover:bg-gray-100 rounded-lg cursor-pointer">
                                    {edit ?
                                        <p>{t("Cancel")}</p> :
                                        <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M3.66406 21V16.75L16.8641 3.575C17.0641 3.39167 17.2851 3.25 17.5271 3.15C17.7691 3.05 18.0231 3 18.2891 3C18.5551 3 18.8134 3.05 19.0641 3.15C19.3147 3.25 19.5314 3.4 19.7141 3.6L21.0891 5C21.2891 5.18333 21.4351 5.4 21.5271 5.65C21.6191 5.9 21.6647 6.15 21.6641 6.4C21.6641 6.66667 21.6184 6.921 21.5271 7.163C21.4357 7.405 21.2897 7.62567 21.0891 7.825L7.91406 21H3.66406ZM18.2641 7.8L19.6641 6.4L18.2641 5L16.8641 6.4L18.2641 7.8Z" fill="black" />
                                        </svg>}
                                </div>
                            </ShowUI>
                            <div className="flex gap-4">
                                <button onClick={() => { setConfirmAction(() => () => handleForward(
                                    formState.status === SECTOR_DIRECTIVES_REQUEST_STATUS.under_review.value ? SECTOR_DIRECTIVES_REQUEST_STATUS.not_forwarded.value :
                                        SECTOR_DIRECTIVES_REQUEST_STATUS.forwarded_to_director.value)); setIsModalOpen(true); }} type="button" className="bg-[#0A1D39] text-white rounded-lg px-4">{t("Forward")}</button>
                            </div>
                        </div>
                    </ShowUI>
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.director_forward_to_coordinator.value}
                        currentStatus={formState.status}
                        forwardStatus={formState.forward_status}
                    >
                        <div className="flex ml-auto gap-4">
                            <div onClick={() => setEdit(!edit)} className="px-2 hover:bg-gray-100 rounded-lg cursor-pointer">
                                {edit ?
                                    <p>{t("Cancel")}</p> :
                                    <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M3.66406 21V16.75L16.8641 3.575C17.0641 3.39167 17.2851 3.25 17.5271 3.15C17.7691 3.05 18.0231 3 18.2891 3C18.5551 3 18.8134 3.05 19.0641 3.15C19.3147 3.25 19.5314 3.4 19.7141 3.6L21.0891 5C21.2891 5.18333 21.4351 5.4 21.5271 5.65C21.6191 5.9 21.6647 6.15 21.6641 6.4C21.6641 6.66667 21.6184 6.921 21.5271 7.163C21.4357 7.405 21.2897 7.62567 21.0891 7.825L7.91406 21H3.66406ZM18.2641 7.8L19.6641 6.4L18.2641 5L16.8641 6.4L18.2641 7.8Z" fill="black" />
                                    </svg>}
                            </div>
                            <div className="flex gap-4">
                                <button onClick={() => { setConfirmAction(() => () => handleForward(
                                   formState.status === SECTOR_DIRECTIVES_REQUEST_STATUS.back_to_head.value ? SECTOR_DIRECTIVES_REQUEST_STATUS.forwarded_to_head.value :
                                       SECTOR_DIRECTIVES_REQUEST_STATUS.forwarded_to_coordinator.value)); setIsModalOpen(true); }} type="button" className="bg-[#0A1D39] text-white rounded-lg px-4">{t("Forward")}</button>
                            </div>
                        </div>
                    </ShowUI>
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.coordinator_forward_to_prosecutor.value}
                        currentStatus={formState.status}
                        forwardStatus={formState.forward_status}
                    >
                        <div className="flex ml-auto gap-4">
                            <ShowUI
                                ui_key={LEGISLATIVE_DRAFTING_UI_RULES.edit_coordinator_forward_to_prosecutor.value}
                                currentStatus={formState.status}
                                forwardStatus={formState.forward_status}
                            >
                                <div onClick={() => setEdit(!edit)} className="px-2 hover:bg-gray-100 rounded-lg cursor-pointer">
                                    {edit ?
                                        <p>{t("Cancel")}</p> :
                                        <svg width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                            <path d="M3.66406 21V16.75L16.8641 3.575C17.0641 3.39167 17.2851 3.25 17.5271 3.15C17.7691 3.05 18.0231 3 18.2891 3C18.5551 3 18.8134 3.05 19.0641 3.15C19.3147 3.25 19.5314 3.4 19.7141 3.6L21.0891 5C21.2891 5.18333 21.4351 5.4 21.5271 5.65C21.6191 5.9 21.6647 6.15 21.6641 6.4C21.6641 6.66667 21.6184 6.921 21.5271 7.163C21.4357 7.405 21.2897 7.62567 21.0891 7.825L7.91406 21H3.66406ZM18.2641 7.8L19.6641 6.4L18.2641 5L16.8641 6.4L18.2641 7.8Z" fill="black" />
                                        </svg>}
                                </div>
                            </ShowUI>
                            <div className="flex gap-4">
                                <button onClick={() => { setConfirmAction(() => () => handleForward(
                                   formState.status === SECTOR_DIRECTIVES_REQUEST_STATUS.back_to_director.value ? SECTOR_DIRECTIVES_REQUEST_STATUS.forwarded_to_coordinator.value :
                                       SECTOR_DIRECTIVES_REQUEST_STATUS.assigned_to_prosecutor.value)); setIsModalOpen(true); }} type="button" className="bg-[#0A1D39] text-white rounded-lg px-4">{t("Forward")}</button>
                            </div>
                        </div>
                    </ShowUI>
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.coordinator_forward_to_head.value}
                        currentStatus={formState.status}
                        forwardStatus={formState.forward_status}
                    >
                        <div className="flex ml-auto gap-4">
                            <div className="flex gap-4">
                                <button onClick={() => { setConfirmAction(() => () => handleForward(SECTOR_DIRECTIVES_REQUEST_STATUS.forwarded_to_head.value)); setIsModalOpen(true); }}
                                   type="button" className="bg-[#0A1D39] text-white rounded-lg px-4">{t("Forward")}</button>
                            </div>
                        </div>
                    </ShowUI>
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.additional_info_action.value}
                        currentStatus={formState.status}
                        forwardStatus={formState.forward_status}
                    >
                        <div className="flex ml-auto gap-4">
                            <div className="flex gap-4">
                                <button onClick={() => { setConfirmAction(() => () => handleForward(SECTOR_DIRECTIVES_REQUEST_STATUS.not_forwarded.value)); setIsModalOpen(true); }} type="button" className="bg-[#0A1D39] text-white rounded-lg px-4">{t("Forward")}</button>
                            </div>
                        </div>
                    </ShowUI>
                    {formState.status === SECTOR_DIRECTIVES_REQUEST_STATUS.waiting_for_additional_info.value &&
                        formState.forward_status === SECTOR_DIRECTIVES_REQUEST_STATUS.not_forwarded.value &&
                        contextData?.user?.roles?.some((r: string) => r === "LegalResearchProsecutor") && (
                            <div className="flex ml-auto gap-4">
                                <div className="flex gap-4">
                                    <button onClick={() => { setConfirmAction(() => () => handleForwardToStakeholder()); setIsModalOpen(true); }} type="button" className="bg-[#0A1D39] text-white rounded-lg px-4">{t("ForwardToStakeholder")}</button>
                                </div>
                            </div>
                        )}
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.prosecutor_forward_to_coordinator.value}
                        currentStatus={formState.status}
                        forwardStatus={formState.forward_status}
                    >
                        <div className="flex ml-auto gap-4">
                            <div className="flex gap-4">
                                <button onClick={() => { setConfirmAction(() => () => handleForward(SECTOR_DIRECTIVES_REQUEST_STATUS.forwarded_to_coordinator.value)); setIsModalOpen(true); }}
                                   type="button" className="bg-[#0A1D39] text-white rounded-lg px-4">{t("Forward")}</button>
                            </div>
                        </div>
                    </ShowUI>
                    <div>
                    </div> {/* For spacing */}
                </div>
                {contextData?.user?.roles?.some((p) => p === "LRDIFUDDirector") &&
                    ((formState?.status === "archived" && formState?.forward_status === "forwarded_to_director") ||
                    CanShow(LEGISLATIVE_DRAFTING_UI_RULES.publish.value, formState.status, formState.forward_status)) && (
                    <BroadcastCard
                        onClick={() => handleBroadcast(!formState.is_published)} 
                        isPublished={formState.is_published || false}
                        title={t("BroadcastToPublicWebsite")}
                        description={t("directives.submit_request_desc")}
                        additionalInfo={t("directives.submit_request_desc_alt")}
                    />
                )}
                {formState?.documents?.find((document: any) => document.document_type === "dir_reg_written_comments") &&
                    (contextData?.user?.user_type !== "external" || (formState?.status === "archived" && formState?.forward_status === "forwarded_to_director")) &&
                    <div className="flex-1 bg-white mb-4 rounded-lg border border-gray-200 shadow-md overflow-hidden">
                        <div className="bg-gradient-to-r from-[#0A1D39] to-[#13294d] px-5 py-3 flex items-center justify-between">
                            <div>
                                <p className="text-[10px] uppercase tracking-widest text-amber-300 font-semibold">{t("OAG") || "OAG"}</p>
                                <p className="text-white font-semibold text-base">{t("SummaryWritten")}</p>
                            </div>
                            {!(formState?.status === "archived" && formState?.forward_status === "forwarded_to_director") && (
                                <button
                                    onClick={() => setEditSummary(true)}
                                    className="text-sm text-white/90 hover:text-white flex items-center gap-1"
                                >
                                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M3.66406 21V16.75L16.8641 3.575C17.0641 3.39167 17.2851 3.25 17.5271 3.15C17.7691 3.05 18.0231 3 18.2891 3C18.5551 3 18.8134 3.05 19.0641 3.15C19.3147 3.25 19.5314 3.4 19.7141 3.6L21.0891 5C21.2891 5.18333 21.4351 5.4 21.5271 5.65C21.6191 5.9 21.6647 6.15 21.6641 6.4C21.6641 6.66667 21.6184 6.921 21.5271 7.163C21.4357 7.405 21.2897 7.62567 21.0891 7.825L7.91406 21H3.66406ZM18.2641 7.8L19.6641 6.4L18.2641 5L16.8641 6.4L18.2641 7.8Z" fill="#ffffff" />
                                    </svg>
                                    {t("Edit")}
                                </button>
                            )}
                        </div>
                        <div className="p-5">
                        <p className="text-[#747171] font-normal text-xs mb-3">{formatDay(formState?.created_at).rest}</p>
                        <p className="font-regular text-base text-[#606060]">{formState?.summary_of_comments}</p>

                        {/* Show latest file */}
                        <DocumentViewerModal
                            documents={[formState?.documents?.find((document: any) => document.document_type === "dir_reg_written_comments")]}
                            baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_research/legal-research-request-documents/document/`}
                        />

                        {/* Accordion for previous files - hidden for external users */}
                        {contextData?.user?.user_type !== "external" && formState?.documents?.filter((document: any) => document.document_type === "dir_reg_written_comments").length > 1 && (
                            <div className="mt-4">
                                <button
                                    onClick={() => setShowPreviousFiles(!showPreviousFiles)}
                                    className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-800"
                                >
                                    <svg
                                        className={`w-4 h-4 transition-transform ${showPreviousFiles ? 'rotate-180' : ''}`}
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                        xmlns="http://www.w3.org/2000/svg"
                                    >
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                    </svg>
                                    {t("PreviousVersions")} ({formState?.documents?.filter((document: any) => document.document_type === "dir_reg_written_comments").length - 1})
                                </button>
                                {showPreviousFiles && (
                                    <div className="mt-2 space-y-2">
                                        {formState?.documents?.filter((document: any) => document.document_type === "dir_reg_written_comments").slice(1).map((doc: any) => (
                                            <div key={doc.id} className="text-sm text-gray-600">
                                                <DocumentViewerModal
                                                    documents={[doc]}
                                                    baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_research/legal-research-request-documents/document/`}
                                                />
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                        </div>
                    </div>
                }
                {/* Request for Additional Info / Summary Written Comments — below the Summary Written card */}
                <ShowUI
                    ui_key={LEGISLATIVE_DRAFTING_UI_RULES.draft_and_additional_file.value}
                    currentStatus={"all"}
                    forwardStatus={formState.forward_status}
                >
                    <ProsecutorRemark setFormState={setFormState} formState={formState} />
                </ShowUI>
                <ShowUI
                    ui_key={LEGISLATIVE_DRAFTING_UI_RULES.final_approval.value}
                    currentStatus={formState.status}
                    forwardStatus={formState.forward_status}
                >
                    <LegislativeRespondBackForm
                        request={formState}
                        setLegalResearch={setFormState}
                        isOpen={isRespondBackFormOpen}
                        onClose={() => setIsRespondBackFormOpen(false)}
                        forward={SECTOR_DIRECTIVES_REQUEST_STATUS.audit_responded.value}
                        type="directive"
                    />
                    <div className="flex gap-4">
                        <button onClick={handleRespondBackClick} className="p-4 flex-1 bg-gray-200 text-gray-800 rounded-lg">{t("RespondBack")}</button>
                        <button onClick={handleFinalApprove} className="p-4 flex-1 bg-[#0A1D39] text-white rounded-lg">{t("FinalApproveDraftLaw")}</button>
                    </div>
                </ShowUI>
                <div className="flex-1 overflow-y-scroll scrollbar-hide">
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.forward_darft.value}
                        currentStatus={formState.status}
                        forwardStatus={formState.forward_status}
                    >
                        <LegislativeRespondBackForm
                            request={formState}
                            setLegalResearch={setFormState}
                            isOpen={isRespondBackFormOpen}
                            onClose={() => setIsRespondBackFormOpen(false)}
                            forward={SECTOR_DIRECTIVES_REQUEST_STATUS.draft_responded.value}
                            type="directive"
                        />
                        <div className="mt-4 pb-8 border-b border-gray-200 flex flex-col sm:flex-row justify-between space-y-4 sm:space-y-0 sm:space-x-4">
                            <button
                                type="button"
                                className="flex-1 px-6 py-3 rounded-md bg-gray-200 text-gray-800 font-semibold hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-300"
                                onClick={handleRespondBackClick}
                            >
                                {loading ? (
                                    <div className="flex items-center justify-center">
                                        {LoadingIcon} {t("Submitting")}
                                    </div>
                                ) : (
                                    t("RespondBack")
                                )}
                            </button>
                            <div
                               onClick={() => { setConfirmAction(() => () => handleForward(SECTOR_DIRECTIVES_REQUEST_STATUS.forwarded_to_director.value)); setIsModalOpen(true); }}
                               className="flex-1 text-center cursor-pointer px-6 py-3 rounded-md bg-[#0A1D39] text-white font-semibold hover:bg-[#13294d] focus:outline-none focus:ring-2 focus:ring-gray-600"
                           >
                                {loading ? (
                                    <div className="flex items-center justify-center">
                                        {LoadingIcon} {t("Submitting")}
                                    </div>
                                ) : (
                                    t("ApproveAndForwardToDirector")
                                )}
                            </div>
                        </div>
                    </ShowUI>
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.forward_darft_to_director.value}
                        currentStatus={formState.status}
                        forwardStatus={formState.forward_status}
                    >
                        <LegislativeRespondBackForm
                            request={formState}
                            setLegalResearch={setFormState}
                            isOpen={isRespondBackFormOpen}
                            onClose={() => setIsRespondBackFormOpen(false)}
                            forward={SECTOR_DIRECTIVES_REQUEST_STATUS.draft_responded.value}
                            type="directive"
                        />
                        <div className="mt-4 pb-8 border-b border-gray-200 flex flex-col sm:flex-row justify-between space-y-4 sm:space-y-0 sm:space-x-4">
                            <button
                                type="button"
                                className="flex-1 px-6 py-3 rounded-md bg-gray-200 text-gray-800 font-semibold hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-300"
                                onClick={handleRespondBackClick}
                            >
                                {loading ? (
                                    <div className="flex items-center justify-center">
                                        {LoadingIcon} {t("Submitting")}
                                    </div>
                                ) : (
                                    t("RespondBack")
                                )}
                            </button>
                            <div
                               onClick={() => { setConfirmAction(() => () => handleForward(
                                   formState.forward_status === SECTOR_DIRECTIVES_REQUEST_STATUS.forwarded_to_coordinator.value
                                       ? SECTOR_DIRECTIVES_REQUEST_STATUS.forwarded_to_director.value
                                       : SECTOR_DIRECTIVES_REQUEST_STATUS.forwarded_to_head.value)); setIsModalOpen(true); }}
                               className="flex-1 text-center cursor-pointer px-6 py-3 rounded-md bg-[#0A1D39] text-white font-semibold hover:bg-[#13294d] focus:outline-none focus:ring-2 focus:ring-gray-600"
                           >
                                {loading ? (
                                    <div className="flex items-center justify-center">
                                        {LoadingIcon} {t("Submitting")}
                                    </div>
                                ) : (
                                    formState.forward_status === SECTOR_DIRECTIVES_REQUEST_STATUS.forwarded_to_coordinator.value
                                        ? t("ApproveAndForwardToDirector")
                                        : t("ApproveAndForwardToHead")
                                )}
                            </div>
                        </div>
                    </ShowUI>
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.forward_darft_to_head.value}
                        currentStatus={formState.status}
                        forwardStatus={formState.forward_status}
                    >
                        <LegislativeRespondBackForm
                            request={formState}
                            setLegalResearch={setFormState}
                            isOpen={isRespondBackFormOpen}
                            onClose={() => setIsRespondBackFormOpen(false)}
                            forward={SECTOR_DIRECTIVES_REQUEST_STATUS.draft_responded.value}
                            type="directive"
                        />

                        <div className="mt-4 pb-8 border-b border-gray-200 flex flex-col sm:flex-row justify-between space-y-4 sm:space-y-0 sm:space-x-4">
                            <button
                                type="button"
                                className="flex-1 px-6 py-3 rounded-md bg-gray-200 text-gray-800 font-semibold hover:bg-gray-300 focus:outline-none focus:ring-2 focus:ring-gray-300"
                                onClick={handleRespondBackClick}
                            >
                                {loading ? (
                                    <div className="flex items-center justify-center">
                                        {LoadingIcon} {t("Submitting")}
                                    </div>
                                ) : (
                                    t("RespondBack")
                                )}
                            </button>
                            <div
                               onClick={() => { setConfirmAction(() => handleDraftApprove); setIsModalOpen(true); }}
                               className="flex-1 text-center cursor-pointer px-6 py-3 rounded-md bg-[#0A1D39] text-white font-semibold hover:bg-[#13294d] focus:outline-none focus:ring-2 focus:ring-gray-600"
                           >
                                {loading ? (
                                    <div className="flex items-center justify-center">
                                        {LoadingIcon} {t("Submitting")}
                                    </div>
                                ) : (
                                    t("Approve")
                                )}
                            </div>
                        </div>
                    </ShowUI>
                    {/* Documents */}
                    <div className="mt-6 space-y-4 border border-gray-400 p-4 mb-8 rounded-lg">
                        {formState?.documents?.find((document: any) => document.document_type === "directive_reg") &&
                            <>
                                {formState?.registry_number &&
                                    <p className="text-[#432101AB] font-normal text-base">{t("RegNumber")}: {formState.registry_number}</p>
                                }
                                <p className="text-[#432101AB] font-normal text-base">{t("directives.letter_ref_number_label")}: {formState?.request_letter_reference_number}</p>
                                {formState?.documents?.find((document: any) => document.document_type === "directive_reg") &&
                                    <DocumentViewerModal
                                        documents={formState?.documents.filter((document: any) => document.document_type === "directive_reg")}
                                        baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_research/legal-research-request-documents/document/`}
                                    />
                                }
                            </>
                        }
                        {formState?.documents?.find((document: any) => document.document_type === "reg_directive_request_letter") &&
                            <DocumentViewerModal
                                documents={formState?.documents.filter((document: any) => document.document_type === "reg_directive_request_letter")}
                                baseUrl={`${process.env.REACT_APP_API_BASE_URL}legal_research/legal-research-request-documents/document/`}
                            />
                        }
                        <div className="mb-8">
                            <p className="text-[#432101AB] font-normal text-base">{t("ExplanatoryNote")}</p>
                            <p className="text-gray-500 font-medium mb-2">
                                {formState?.explanatory_notes}
                            </p>
                        </div>
                    </div>
                    {formState?.prosecutor_assignments &&
                        formState?.prosecutor_assignments.length !== 0 &&
                        contextData?.user?.user_type !== "external" &&
                        (<div className="mt-6 mb-6 flex bg-gray-50 rounded-lg border border-gray-200">
                            <ActionAvatars
                                title={t("TeamList")}
                                users={formState?.prosecutor_assignments}
                                prosecutor={true}
                            />
                            {contextData?.user?.roles?.some((p) => p === "LegalResearchCoordinator" || p === "LRDIFUDDirector") && (!edit ?
                                <svg onClick={() => setEdit(true)} className="my-auto mr-2 hover:bg-gray-300 rounded-lg cursor-pointer" width="25" height="24" viewBox="0 0 25 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M3.66406 21V16.75L16.8641 3.575C17.0641 3.39167 17.2851 3.25 17.5271 3.15C17.7691 3.05 18.0231 3 18.2891 3C18.5551 3 18.8134 3.05 19.0641 3.15C19.3147 3.25 19.5314 3.4 19.7141 3.6L21.0891 5C21.2891 5.18333 21.4351 5.4 21.5271 5.65C21.6191 5.9 21.6647 6.15 21.6641 6.4C21.6641 6.66667 21.6184 6.921 21.5271 7.163C21.4357 7.405 21.2897 7.62567 21.0891 7.825L7.91406 21H3.66406ZM18.2641 7.8L19.6641 6.4L18.2641 5L16.8641 6.4L18.2641 7.8Z" fill="black" />
                                </svg> : <p onClick={() => setEdit(false)} className="my-auto mr-2 hover:bg-gray-300 rounded-lg px-4 cursor-pointer">{t("Cancel")}</p>)}
                            <svg onClick={() => setViewTeam(!viewTeam)} className="mr-2 ml-auto my-auto cursor-pointer" xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                                <mask id="mask0_9_31087" maskUnits="userSpaceOnUse" x="1" y="5" width="22" height="14">
                                    <path d="M12 18C17.523 18 22 12 22 12C22 12 17.523 6 12 6C6.477 6 2 12 2 12C2 12 6.477 18 12 18Z" fill="white" stroke="white" stroke-width="1.33333" stroke-linejoin="round" />
                                    <path d="M12 14.5C12.663 14.5 13.2989 14.2366 13.7678 13.7678C14.2366 13.2989 14.5 12.663 14.5 12C14.5 11.337 14.2366 10.7011 13.7678 10.2322C13.2989 9.76339 12.663 9.5 12 9.5C11.337 9.5 10.7011 9.76339 10.2322 10.2322C9.76339 10.7011 9.5 11.337 9.5 12C9.5 12.663 9.76339 13.2989 10.2322 13.7678C10.7011 14.2366 11.337 14.5 12 14.5Z" fill="black" stroke="black" stroke-width="1.33333" stroke-linejoin="round" />
                                </mask>
                                <g mask="url(#mask0_9_31087)">
                                    <path d="M0 0H24V24H0V0Z" fill="#EFC83D" />
                                </g>
                            </svg>
                        </div>)}
                    {edit &&
                        contextData?.user?.roles?.some((p) => p === "LegalResearchCoordinator" || p === "LRDIFUDDirector") &&
                        <CoordinatorRemark type="register_directives" setEdit={setEdit} setFormState={setFormState} formState={formState} />
                    }
                    <br />
                    {!!formState.additional_infos[0] &&
                        (contextData?.user?.user_type === "external" ||
                            contextData?.user?.roles?.some((p) => p === "LegalResearchCoordinator") ||
                            (contextData?.user?.roles?.some((p) => p === "LegalResearchProsecutor") &&
                                formState?.prosecutor_assignments?.find((prosecutor: any) => prosecutor.prosecutor_user_id === contextData?.user?.user_id)?.is_team_leader)
                        ) &&
                        <div className="flex-1 bg-white mb-4 p-4 rounded-lg border border-gray-400">
                            <div className="pb-2 border-b border-gray-200 mb-4">
                                <p>{t("AdditionalInformationRequest")}</p>
                            </div>
                            <p className="text-[#747171] font-normal text-xs mb-3">{formatDay(formState?.additional_infos[0].requested_at).rest}</p>
                            <p className="font-regular text-base text-[#606060]">{formState?.additional_infos[0].request_summary}</p>
                            {formState?.additional_infos[0]?.documents?.find((document: any) => document.document_type === "reg_dir_additional_request_file") &&
                                <DocumentViewerModal
                                    documents={formState?.additional_infos[0].documents.filter((document: any) => document.document_type === "reg_dir_additional_request_file")}
                                    baseUrl={`${process.env.REACT_APP_API_BASE_URL}//api/accuracy_government_sector/directive-respond-back-feedback-files/document`}
                                />
                            }
                            {!!formState.additional_infos[0] && !(formState.status === "Waiting for Additional Information") &&
                                <>
                                    <div className="pb-2 border-b border-gray-200 mb-4">
                                        <p>{t("AdditionalInformationResponse")}</p>
                                    </div>
                                    <p className="text-[#747171] font-normal text-xs mb-3">{formatDay(formState?.additional_infos?.[0]?.responded_at).rest}</p>
                                    <p className="font-regular w-[55ch] text-justify text-base text-[#606060]">{formState?.additional_infos?.[0]?.response_note}</p>
                                    {formState?.additional_infos[0].documents?.find((document: any) => document.document_type === "reg_dir_additional_response_file") &&
                                        <DocumentViewerModal
                                            documents={formState?.additional_infos[0].documents.filter((document: any) => document.document_type === "reg_dir_additional_response_file")}
                                            baseUrl={`${process.env.REACT_APP_API_BASE_URL}/api/directive_registration/directive-additional-info-documents/document`}
                                        />
                                    }
                                </>
                            }
                        </div>
                    }
                    {/* Additional Details */}
                    {formState.requested_by !== "internal" &&
                        contextData?.user?.user_type === "internal" &&
                        <>
                            <div>
                                <div className="flex">
                                    <p className="text-[#432101AB] font-normal text-base">{t("Stakeholder")}</p>
                                    <p className="ml-auto text-[#0A1D39] font-semibold text-base">{formState?.organization_name}</p>
                                </div>
                                <hr className="w-full border-t-2 border-gray-300 my-4" />
                                <div className="flex">
                                    <p className="text-[#432101AB] font-normal text-base">{t("StakeholderType")}</p>
                                    <p className="ml-auto text-[#0A1D39] font-semibold text-base">{formState?.organization_type}</p>
                                </div>
                                <hr className="w-full border-t-2 border-gray-300 my-4" />
                                <div className="flex">
                                    <p className="text-[#432101AB] font-normal text-base">{t("Date")}</p>
                                    <p className="ml-auto text-[#0A1D39] font-semibold text-base">{formatDate(formState?.created_at)}</p>
                                </div>
                                <hr className="w-full border-t-2 border-gray-300 my-4" />
                                <div className="flex">
                                    <p className="text-[#432101AB] font-normal text-base">{t("Address")}</p>
                                    <p className="ml-auto text-[#0A1D39] font-semibold text-base">{formState?.organization_state}, {formState?.organization_zone}, {formState?.organization_kebele}</p>
                                </div>
                                <hr className="w-full border-t-2 border-gray-300 my-4" />
                                <div className="flex">
                                    <p className="text-[#432101AB] font-normal text-base">{t("Email")}</p>
                                    <p className="ml-auto text-[#0A1D39] font-semibold text-base">{formState?.email_user_organization}</p>
                                </div>
                                <hr className="w-full border-t-2 border-gray-300 my-4" />
                            </div>
                            <br />
                        </>}


                    {/* Remark Section */}
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.internal_ui.value}
                        currentStatus={"all"}
                        forwardStatus={"all"}
                    >
                        {formState.remarks && formState?.remarks?.find((remark: any) => remark.role === "head") &&
                            <div>
                                <p className="text-[#432101AB] font-normal text-base">{t("DirectorRemark")}</p>
                                <p className="text-gray-500 font-medium mb-2">
                                    {formState?.remarks.filter((remark: any) => remark.role === "head").slice().reverse()[0]?.remark}
                                </p>
                                <br />
                            </div>
                        }
                        {formState.remarks && formState?.remarks?.find((remark: any) => remark.role === "director") &&
                            <div>
                                <p className="text-[#432101AB] font-normal text-base">{t("CoordinatorRemark")}</p>
                                <p className="text-gray-500 font-medium mb-2">
                                    {formState?.remarks.filter((remark: any) => remark.role === "director").slice().reverse()[0]?.remark}
                                </p>
                                <br />
                            </div>
                        }
                        {formState.remarks && formState?.remarks?.find((remark: any) => remark.role === "coordinator") &&
                            <div>
                                <p className="text-[#432101AB] font-normal text-base">{t("Remark")}</p>
                                <p className="text-gray-500 font-medium mb-2">
                                    {formState?.remarks.filter((remark: any) => remark.role === "coordinator").slice().reverse()[0]?.remark}
                                </p>
                                <br />
                            </div>
                        }
                    </ShowUI>
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.edit_head_forward_to_director.value}
                        currentStatus={formState.status}
                        forwardStatus={formState.forward_status}
                    >
                        <HeadRemark type="register_directives" setEdit={setEdit} setFormState={setFormState} formState={formState} />
                    </ShowUI>
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.edit_director_forward_to_coordinator.value}
                        currentStatus={formState.status}
                        forwardStatus={formState.forward_status}
                    >
                        <DirectorRemark type="register_directives" setEdit={setEdit} setFormState={setFormState} formState={formState} />
                    </ShowUI>
                    <ShowUI
                        ui_key={LEGISLATIVE_DRAFTING_UI_RULES.edit_coordinator_forward_to_prosecutor.value}
                        currentStatus={formState.status}
                        forwardStatus={formState.forward_status}
                    >
                        <CoordinatorRemark type="register_directives" setEdit={setEdit} setFormState={setFormState} formState={formState} />
                    </ShowUI>
                </div>
            </div>
            {/*
            <div className="flex justify-between bg-white rounded-lg p-6 mt-6 shadow-md">
                <div className="flex flex-col gap-2">
                    <p className="text-[#A7A7A7] font-medium text-sm">Date</p>
                    <p className="text-sm font-normal">{formatDay(formState?.created_at).rest}</p>
                </div>
                <div className="flex flex-col gap-2">
                    <p className="text-[#A7A7A7] font-medium text-sm">Requested By</p>
                    <p className="text-sm font-normal">{formState?.requested_by}</p>
                </div>
                <div className="flex flex-col gap-2">
                    <p className="text-[#A7A7A7] font-medium text-sm">Assigned By</p>
                    <p className="text-sm font-normal">{formState?.assigned_by ?? "----------"}</p>
                </div>
                <div className="flex flex-col gap-2">
                    <p className="text-[#A7A7A7] font-medium text-sm">Assigned to</p>
                    <p className="text-sm font-normal">{formState?.assigned_to ?? "----------"}</p>
                </div>
            </div>
            */}
            {publicFeedback &&
                (<PublicFeedback
                    data={formState}
                    setLegalResearch={setFormState}
                    isOpen={publicFeedback}
                    onClose={() => setPublicFeedback(false)}
                />)
            }
            {uploadAmend &&
                (<UploadAmendedFile
                    data={formState}
                    setLegalResearch={setFormState}
                    isOpen={uploadAmend}
                    onClose={() => setUploadAmend(false)}
                />)
            }
            {viewTeam &&
                <TeamView teamMembers={formState.prosecutor_assignments} onClose={() => setViewTeam(false)} />}
            <ConfirmationModal1 type="success" isOpen={isModalOpen} isLoading={false} onCancel={() => setIsModalOpen(false)} onConfirm={() => { confirmAction(); setIsModalOpen(false); }} />
            <UploadDraftLaw
                request={formState}
                setLegalResearch={setFormState}
                isOpen={editSummary}
                onClose={() => setEditSummary(false)}
                onUploadProposal={() => {}}
                type="register_directives"
            />
        </div>
    );
}

export default LegalAdvisoryDetail;
